import {
	Button,
	ConfirmPopover,
	Icon,
	LinkButton,
	MuiAlert,
	MuiMenuItem,
	ResponsiveDrawer,
	TextField,
	Typography,
	useDebounce
} from '@fox/atoms';
import { ResponsiveTable } from '@fox/data-grid';
import { useSubscribeTopicWithHandler } from '@fox/extensions';
import { FlexColumn, FlexColumnSpaced, FlexRow, FlexRowSpaced } from '@fox/layout';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useContentContext } from '../../content-context';
import { CustomFieldValueConverter } from '../membership-form-template/fields';
import { useCreateOrgMembership, useRemoveOrgMembership } from '../mutation-hooks/mutation-implementations';
import { useRepresentativeRelationsData } from '../query-hooks/query-implementations';
import { EntityInfo, Relation } from './contracts';
import { useRepresentativesContext } from './representatives-context';
import { representativeEntityId } from './util';

export const SearchTitleWrapper = styled.div`
	margin-bottom: 1rem;
	align-items: center;
	gap: 1rem;
	flex-direction: column;
	display: flex;
	width: 400px;
	text-wrap: nowrap;
	align-items: flex-start;
	@media (max-width: 800px) {
		width: 100%;
		align-items: flex-start;
		text-wrap: initial;
	}
`;

export function SourceOrganisation({ source }: { source: EntityInfo }) {
	const { unit, getSourceData, updateSourceData, optimisticallyAddRelation, optimisticallyRemoveRelation } = useRepresentativesContext();
	const { contentId, requestToken } = useContentContext();
	const [page, setPage] = useState<number>(1);
	const PAGE_SIZE = 10;
	const [search, setSearch] = useState<string>('');
	const debouncedSearch = useDebounce(search, 1000);
	const { data, error, loading, refetch } = useRepresentativeRelationsData({
		contentId,
		requestToken,
		representativeUnitId: unit.id,
		sfOrgId: source.id,
		page: page - 1,
		pageSize: PAGE_SIZE,
		search: debouncedSearch,
		triggers: ['search', 'page'],
		isSource: true
	});

	// Update context data when query data changes (but not during optimistic updates)
	React.useEffect(() => {
		if (data) {
			updateSourceData(source.id, data);
		}
	}, [data, source.id, updateSourceData]);

	// Determine if we're in initial loading state (loading and no data in context yet)
	const contextData = getSourceData(source.id);
	const effectiveData = contextData || data;
	const isInitialLoading = loading && !contextData;

	useSubscribeTopicWithHandler(representativeEntityId({ unitId: unit.id, entityId: source.id, type: 'source' }), () => {
		refetch();
	});

	// Build per-target cap map for this source (source/target specific caps)
	const delegateCapsByTarget = React.useMemo(() => {
		const map: Record<string, number> = {};
		// sourceDelegateCaps now supplied by query result (data)
		effectiveData?.sourceDelegateCaps
			?.filter(c => c.sourceId === source.id)
			?.forEach(c => {
				if (c.targetId) {
					map[c.targetId] = c.cap;
				}
			});
		return map;
	}, [effectiveData?.sourceDelegateCaps, source.id]);

	// Counts of delegates this source already has for each target
	const delegateCountsByTarget = React.useMemo(() => {
		const map: Record<string, number> = {};
		effectiveData?.data?.forEach(person => {
			person.relations?.forEach(rel => {
				map[rel.relationTargetId] = (map[rel.relationTargetId] ?? 0) + 1;
			});
		});
		return map;
	}, [effectiveData?.data]);

	const peopleColumns = unit.entityFields.map(f => {
		return {
			title: f.displayName,
			dataIndex: f.customFieldId,
			render: record => {
				const customField = record.personFields?.find(field => field.customFieldId === f.customFieldId);
				const value = CustomFieldValueConverter[customField.type]?.(customField?.value, customField?.meta);
				return customField.type === 'check-box' ? (
					customField.value === '1' ? (
						<Icon iconName="Done" />
					) : (
						<Icon iconName="Cancel" />
					)
				) : (
					<Typography>{value || '-'}</Typography>
				);
			}
		};
	});
	return (
		<FlexColumn style={{ marginTop: '1rem' }}>
			<SearchTitleWrapper>
				<Typography variant="h5">{source.name}</Typography>
				<TextField
					value={search ?? ''}
					onChange={e => setSearch(e.target.value)}
					autoFocus
					fullWidth
					size="small"
					placeholder="Search"
					endAdornment={<Icon iconName="Search" />}
				/>
			</SearchTitleWrapper>
			<DelegateTallyChips
				targets={unit.targetEntities}
				delegateCountsByTarget={delegateCountsByTarget}
				delegateCapsByTarget={delegateCapsByTarget}
			/>
			{error ? (
				<MuiAlert severity="error">{error}</MuiAlert>
			) : (
				<ResponsiveTable<EntityInfo>
					loading={isInitialLoading}
					columns={[
						{ title: 'Name', dataIndex: 'name', showOnMobileRow: true },
						...peopleColumns,
						{ title: 'Actions', isActionColumn: true }
					]}
					rowActionComponent={record => (
						<AddToOrganisationDesktop
							key={`${record.id}_${source.id}`}
							record={record}
							source={source}
							delegateCountsByTarget={delegateCountsByTarget}
							delegateCapsByTarget={delegateCapsByTarget}
						/>
					)}
					mobileRowActionComponent={record => (
						<AddToOrganisationMobile
							key={`${record.id}_${source.id}`}
							record={record}
							source={source}
							delegateCountsByTarget={delegateCountsByTarget}
							delegateCapsByTarget={delegateCapsByTarget}
						/>
					)}
					data={effectiveData?.data}
					paginationProps={{
						page,
						onPageChange: page => setPage(page),
						total: effectiveData?.totalResults ?? 0,
						itemsPerPage: PAGE_SIZE
					}}
				/>
			)}
		</FlexColumn>
	);
}

function ManagePerson({
	record,
	source,
	setOpen,
	isMobile,
	delegateCountsByTarget,
	delegateCapsByTarget
}: {
	record: EntityInfo;
	source: EntityInfo;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	isMobile: boolean;
	delegateCountsByTarget: Record<string, number>;
	delegateCapsByTarget: Record<string, number>;
}) {
	const { unit, getBasePosition, sendUpdates, optimisticallyAddRelation, optimisticallyRemoveRelation } = useRepresentativesContext();
	const { requestToken, contentId } = useContentContext();
	const { mutate: removePerson } = useRemoveOrgMembership({ requestToken });
	const { mutate: createOrgMembership } = useCreateOrgMembership({ requestToken });
	const [targetId, setTargetId] = useState<string>('');
	const [position, setPosition] = useState<string>('');
	const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

	const targets = unit.targetEntities;
	const positions = unit.positions;
	//check if the person has a relationship for each of the available positions with the target org
	const availableTargets = targets?.filter(t => {
		const rawCap = delegateCapsByTarget[t.id];
		const cap = rawCap === 0 ? undefined : rawCap; // 0 => unlimited
		const count = delegateCountsByTarget[t.id] ?? 0;
		const isPersonAlreadyMember = !!record?.relations?.some(r => r.relationTargetId === t.id);
		if (isPersonAlreadyMember) return false;
		if (cap !== undefined && cap > 0 && count >= cap) return false;
		return true;
	});
	//once we have a target id, check available roles
	const availableRolesForTarget = targetId ? positions : positions; // simplified: target guaranteed to have no existing roles

	const memberOfTargets = record?.relations?.filter(m => targets.some(t => t.id === m.relationTargetId));
	const selectedRawCap = targetId ? delegateCapsByTarget[targetId] : undefined;
	const selectedCap = selectedRawCap === 0 ? undefined : selectedRawCap;
	const selectedCount = targetId ? delegateCountsByTarget[targetId] ?? 0 : 0;
	const capReached = !!(
		targetId &&
		selectedCap !== undefined &&
		selectedCap > 0 &&
		selectedCount >= selectedCap &&
		!record?.relations?.some(r => r.relationTargetId === targetId)
	);
	return (
		<FlexColumnSpaced style={{ padding: '1rem' }}>
			{alertMessage && (
				<MuiAlert severity={alertMessage.type} onClose={() => setAlertMessage(null)}>
					{alertMessage.message}
				</MuiAlert>
			)}
			{memberOfTargets?.length > 0 ? (
				<Typography>
					<b>{record.name}</b> is a delegate for the following conference event(s):
				</Typography>
			) : (
				<Typography>
					<b>{record.name}</b> is not yet a delegate to a conference event.
				</Typography>
			)}

			{React.useMemo(() => {
				// Group memberOfTargets by target organization
				const groupedByTarget = memberOfTargets?.reduce((acc, memberOrg) => {
					const targetId = memberOrg.relationTargetId;
					const targetName = memberOrg.relationTargetName;
					
					if (!acc[targetId]) {
						acc[targetId] = {
							targetName,
							relations: []
						};
					}
					acc[targetId].relations.push(memberOrg);
					return acc;
				}, {} as Record<string, { targetName: string; relations: Relation[] }>);

				return Object.entries(groupedByTarget || {}).map(([targetId, targetGroup]) => (
					<div key={targetId} style={{ marginBottom: '1rem' }}>
						<Typography variant="body1" style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
							{targetGroup.targetName}
						</Typography>
						{targetGroup.relations.map(memberOrg => {
							const position = getBasePosition(memberOrg.position);
							return (
								<div key={memberOrg.relationId} style={{ display: 'flex', justifyContent: 'space-between', marginLeft: '1rem', marginBottom: '0.5rem' }}>
									<Typography>{position}</Typography>

									{isMobile ? (
										<RemoveFromOrganisationMobile record={record} relation={memberOrg} />
									) : (
										<ConfirmPopover
											message={
												<Typography>
													Are you sure you would like to remove <b>{record.name}</b> as a <b>{position}</b> from{' '}
													<b>{memberOrg.relationTargetName}</b>?
												</Typography>
											}
											confirm={async () => {
												try {
													const result = await removePerson({ contentId, id: Number(memberOrg.relationId) });
													// Only perform optimistic updates if the mutation was successful
													if (result) {
														optimisticallyRemoveRelation(memberOrg.relationId, source.id, memberOrg.relationTargetId);
														sendUpdates({ relationTargetId: memberOrg.relationTargetId });
														setAlertMessage({ type: 'success', message: `Successfully removed ${record.name} as ${position} from ${memberOrg.relationTargetName}` });
													} else {
														setAlertMessage({ type: 'error', message: 'Failed to remove delegate - no result returned' });
													}
												} catch (error) {
													// Mutation failed, don't do optimistic updates
													console.error('Failed to remove delegate:', error);
													setAlertMessage({ type: 'error', message: `Failed to remove delegate: ${error instanceof Error ? error.message : 'Unknown error'}` });
												}
											}}
										>
											<LinkButton>Remove</LinkButton>
										</ConfirmPopover>
									)}
								</div>
							);
						})}
					</div>
				));
			}, [memberOfTargets, record, contentId, source.id, isMobile, removePerson, optimisticallyRemoveRelation, sendUpdates, getBasePosition])}
			{availableTargets?.length > 0 ? (
				<>
					<Typography>Please select a Conference Event from this list</Typography>
					<TextField
						size="small"
						label="Target organisation"
						value={targetId ?? ''}
						select
						onChange={e => {
							setTargetId(e.target.value);
							setPosition('');
						}}
					>
						{availableTargets?.map(target => {
							const rawCap = delegateCapsByTarget[target.id];
							const cap = rawCap === 0 ? undefined : rawCap; // 0 => unlimited
							const count = delegateCountsByTarget[target.id] ?? 0;
							const atCap = cap !== undefined && cap > 0 && count >= cap;
							return (
								<MuiMenuItem value={target.id} key={target.id} disabled={atCap}>
									{target.name}
									{cap !== undefined && cap > 0 ? ` (${count}/${cap})` : ''}
									{atCap ? ' - full' : ''}
								</MuiMenuItem>
							);
						})}
					</TextField>
				</>
			) : null}
			{targetId && availableRolesForTarget?.length > 0 ? (
				<>
					<Typography>
						Assign <b>{record.name}</b> a position at this Conference Event
					</Typography>
					<TextField
						size="small"
						label="Position"
						value={position ?? ''}
						select
						onChange={e => {
							setPosition(e.target.value);
						}}
					>
						{availableRolesForTarget?.map(position => (
							<MuiMenuItem value={position} key={position}>
								{position}
							</MuiMenuItem>
						))}
					</TextField>
				</>
			) : null}
			{targetId && position ? (
				<Typography>
					You are about to add <b>{record.name}</b> from <b>{source.name}</b> to{' '}
					<b>{availableTargets?.find(t => t.id === targetId)?.name}</b> as <b>{position}</b>
				</Typography>
			) : null}
			<FlexRowSpaced style={{ justifyContent: 'flex-end' }}>
				<Button onClick={() => setOpen(false)} variant="tertiary">
					Cancel
				</Button>
				{targetId && position ? (
					<Button
						disabled={!position || !targetId || capReached || !source.id}
						variant="primary"
						onClick={async () => {
							try {
								const result = await createOrgMembership({
									contentId,
									organizationId: Number(targetId),
									personId: Number(record.id),
									positionName: `${position} (${source.name})`,
									representativeUnitId: unit.id,
									sourceId: source.id
								});
								// Only perform optimistic updates if the mutation was successful
								if (result) {
									const targetName = availableTargets?.find(t => t.id === targetId)?.name ?? '';
									const optimisticRelation: Relation = {
										relationId: `temp-${Date.now()}`,
										position: `${position} (${source.name})`,
										relationTargetId: targetId,
										relationTargetName: targetName
									};
									optimisticallyAddRelation(record.id, optimisticRelation, source.id, targetId);
									sendUpdates({ relationTargetId: targetId });
									setTargetId('');
									setPosition('');
									setAlertMessage({ type: 'success', message: `Successfully added ${record.name} as ${position} to ${targetName}` });
								} else {
									setAlertMessage({ type: 'error', message: 'Failed to add delegate - no result returned' });
								}
							} catch (error) {
								// Mutation failed, don't do optimistic updates
								console.error('Failed to add delegate:', error);
								setAlertMessage({ type: 'error', message: `Failed to add delegate: ${error instanceof Error ? error.message : 'Unknown error'}` });
							}
						}}
					>
						Add
					</Button>
				) : null}
			</FlexRowSpaced>
		</FlexColumnSpaced>
	);
}

function AddToOrganisationDesktop({
	record,
	source,
	delegateCountsByTarget,
	delegateCapsByTarget
}: {
	record: EntityInfo;
	source: EntityInfo;
	delegateCountsByTarget: Record<string, number>;
	delegateCapsByTarget: Record<string, number>;
}) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<>
			<LinkButton sx={{ fontSize: 'initial', lineHeight: 'initial' }} onClick={() => setOpen(true)}>
				Manage
			</LinkButton>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="right"
				title="Manage delegate"
				width={500}
				open={open}
				destroyOnClose
				onClose={() => {
					if (open) setOpen(false);
				}}
			>
				<ManagePerson
					record={record}
					source={source}
					setOpen={setOpen}
					isMobile={false}
					delegateCountsByTarget={delegateCountsByTarget}
					delegateCapsByTarget={delegateCapsByTarget}
				/>
			</ResponsiveDrawer>
		</>
	);
}

function AddToOrganisationMobile({
	record,
	source,
	delegateCountsByTarget,
	delegateCapsByTarget
}: {
	record: EntityInfo;
	source: EntityInfo;
	delegateCountsByTarget: Record<string, number>;
	delegateCapsByTarget: Record<string, number>;
}) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<FlexRow>
			<LinkButton sx={{ fontSize: 'initial', lineHeight: 'initial' }} onClick={() => setOpen(true)}>
				Manage
			</LinkButton>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title="Add person"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					if (open) setOpen(false);
				}}
			>
				<ManagePerson
					record={record}
					source={source}
					setOpen={setOpen}
					isMobile={true}
					delegateCountsByTarget={delegateCountsByTarget}
					delegateCapsByTarget={delegateCapsByTarget}
				/>
			</ResponsiveDrawer>
		</FlexRow>
	);
}

export function RemoveFromOrganisationMobile({ record, relation }: { record: EntityInfo; relation: Relation }) {
	const { requestToken, contentId } = useContentContext();
	const { unit, getBasePosition, sendUpdates, optimisticallyRemoveRelation } = useRepresentativesContext();
	const { mutate: removePerson } = useRemoveOrgMembership({ requestToken });
	const [open, setOpen] = useState<boolean>(false);
	const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
	const position = getBasePosition(relation.position);
	return (
		<FlexRow>
			<LinkButton sx={{ fontSize: 'initial', lineHeight: 'initial' }} onClick={() => setOpen(true)}>
				Remove
			</LinkButton>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title="Remove person"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					if (open) setOpen(false);
				}}
			>
				<FlexColumnSpaced style={{ padding: '1rem' }}>
					{alertMessage && (
						<MuiAlert severity={alertMessage.type} onClose={() => setAlertMessage(null)}>
							{alertMessage.message}
						</MuiAlert>
					)}
					<Typography>
						Are you sure you would like to remove <b>{record.name}</b> as a <b>{position}</b> from{' '}
						<b>{relation.relationTargetName}</b>?
					</Typography>
					<FlexRowSpaced style={{ justifyContent: 'flex-end' }}>
						<Button variant="tertiary" onClick={() => setOpen(false)}>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={async () => {
								try {
									const result = await removePerson({ id: Number(relation.relationId), contentId });
									// Only perform optimistic updates if the mutation was successful
									if (result) {
										// Find the source org using the same approach as target-org component
										const sourceOrg = unit.sourceEntities?.find(s => relation.position.includes(`(${s.name})`));
										optimisticallyRemoveRelation(relation.relationId, sourceOrg?.id || '', relation.relationTargetId);
										sendUpdates({ relationTargetId: relation.relationTargetId, relations: record.relations });
										setAlertMessage({ type: 'success', message: `Successfully removed ${record.name} as ${position} from ${relation.relationTargetName}` });
										setTimeout(() => setOpen(false), 2000); // Auto-close after 2 seconds on success
									} else {
										setAlertMessage({ type: 'error', message: 'Failed to remove delegate - no result returned' });
									}
								} catch (error) {
									// Mutation failed, don't do optimistic updates
									console.error('Failed to remove delegate:', error);
									setAlertMessage({ type: 'error', message: `Failed to remove delegate: ${error instanceof Error ? error.message : 'Unknown error'}` });
								}
							}}
						>
							Remove
						</Button>
					</FlexRowSpaced>
				</FlexColumnSpaced>
			</ResponsiveDrawer>
		</FlexRow>
	);
}

function DelegateTallyChips({
	targets,
	delegateCountsByTarget,
	delegateCapsByTarget
}: {
	targets: EntityInfo[] | undefined;
	delegateCountsByTarget: Record<string, number>;
	delegateCapsByTarget: Record<string, number>;
}) {
	const theme = useTheme();
	return (
		<div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
			{targets?.map(t => {
				const count = delegateCountsByTarget[t.id] ?? 0;
				const rawCap = delegateCapsByTarget[t.id];
				const cap = rawCap === 0 ? undefined : rawCap; // treat 0 as unlimited
				const capReached = cap !== undefined && cap > 0 && count === cap;
				const overCap = cap !== undefined && cap > 0 && count > cap;
				const colors = theme.colors;
				const baseBg = colors.midGrey || theme.palette?.background?.default;
				const reachedBg = colors.grey?.[300]; // neutral indicator when exactly at cap
				const overBg = theme.palette?.error?.main || colors.bad;
				const overText = theme.palette?.common?.white;
				const textColor = overCap ? overText : theme.palette?.text?.primary;
				const borderColor = overCap ? overBg : capReached ? theme.palette?.divider || colors.grey?.[400] : theme.palette?.divider;
				return (
					<div
						key={t.id}
						style={{
							background: overCap ? overBg : capReached ? reachedBg : baseBg,
							border: `1px solid ${borderColor}`,
							borderRadius: '0.25rem',
							padding: '0.25rem 0.5rem',
							color: textColor
						}}
					>
						<Typography variant="body2" style={{ color: textColor }}>
							{t.name}: {count}
							{cap !== undefined && cap > 0 ? ` / ${cap}` : ''}
						</Typography>
					</div>
				);
			})}
		</div>
	);
}
